export type DisplayMode = "d" | "l" | "s"; // dark, light, system

export interface UserAttributes {
  firstName: string;
  lastName?: string;
  displayMode: DisplayMode;
  isUnsubscribedAll: boolean;
  unsubscribedHosts: string[]; // email addresse
  canOwnVents?: boolean;
  isAdmin?: boolean;
  emailAllotment?: number;
  ventLimit?: number;
  guestLimit?: number;
  smsNumber: string; // SMS number of the user
  isOptedOutTextChannel: boolean;
  canTestTextChannel: boolean;
  userId: string; // the ID of the user doc in Firestore
}

export interface Activity {
  description: string;
  limitPerGuest: number;
  limitPerEvent: number;
  hostManaged: boolean; // true if only hosts can update assignments for this activity
}

export type Perm = "m" | "v" | "o"; // manager or viewer or owner
export const formatPerm = (status: Perm | undefined) => {
  switch (status) {
    case "m":
      return "Manager";
    case "v":
      return "Viewer";
    case "o":
      return "Owner";
  }
  return "Viewer";
};

export type CustomFieldType = "t" | "o"; // text or options
export const formatCustomFieldType = (
  fieldType: CustomFieldType | undefined
) => {
  switch (fieldType) {
    case "t":
      return "Text";
    case "o":
      return "Options";
  }
  return "Text";
};

export interface ConfigText {
  required: boolean;
}

export interface ConfigOptions {
  options: string[];
  minOptions: number;
  maxOptions: number;
}

export interface CustomField {
  id: string; // guid
  hidden: boolean;
  name: string;
  description?: string;
  fieldType: CustomFieldType;
  configText?: ConfigText; // for text fields
  configOptions?: ConfigOptions; // for options fields
}

export interface Vent {
  id: string;
  name: string;
  starts: string;
  ends: string;
  description?: string;
  descriptionHtml?: string;
  secondaryMax: number;
  rsvpStart: string;
  rsvpEnd: string;
  rsvpStartDefault: boolean;
  rsvpEndDefault: boolean;
  allowPublicRsvps: boolean;
  allowGuestsView: boolean;
  excludeUndecidedGuests: boolean; // exclude guests with status "undecided"
  excludeDeclinedGuests: boolean; // exclude guests with status "declined"
  location?: string;
  locationUrl?: string;
  updateTime?: string;
  activities: { [key: string]: Activity };
  hosts: { [key: string]: Perm }; // Key is user ID
  isUnsubscribed: boolean;
  customFields: CustomField[];
  timeZone: string; // e.g., "America/New_York" IANA time zones
  allowGuestMessaging: boolean; // allow guests to message each other
  acceptedGuestLimit: number;
}

export const getCustomFieldIndex = (
  customFields: CustomField[],
  customFieldId: string
): number => {
  if (customFields == null) return -1;
  const index = customFields.findIndex((field) => field.id === customFieldId);
  if (index < 0) return -1;
  return index;
};

export type RelativeTo = "rsvpEnd" | "ventStart";
export type TimeUnitOfMeasure = "d" | "w"; // days or weeks
export type RecipientFilterType = "all" | "accepted" | "undecided";

export const recipientFilterTypeToText = (
  recipientFilterType: RecipientFilterType
) => {
  switch (recipientFilterType) {
    case "all":
      return "All Guests";
    case "accepted":
      return "Accepted Guests";
    case "undecided":
      return "Undecided Guests";
    default:
      return "All Guests";
  }
};

export const textToRecipientFilterType = (
  recipientFilterTypeText: string | null
): RecipientFilterType => {
  switch (recipientFilterTypeText) {
    case "All Guests":
      return "all";
    case "Accepted Guests":
      return "accepted";
    case "Undecided Guests":
      return "undecided";
    default:
      return "all";
  }
};

export interface EmailReminder {
  isActive: boolean;
  timeUnitOfMeasure: TimeUnitOfMeasure;
  timeUnits: number;
  recipientFilterType: RecipientFilterType;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  bodyHtml: string;
  textMessageBody: string;
  includeGuests: boolean;
  excludeDeclinedGuests: boolean;
  excludeUndecidedGuests: boolean;
  includeActivities: boolean;
  replyToEmail: string | undefined;
  replyToName: string | undefined;
  emailRemindersRelativeTo: RelativeTo;
  emailReminders: EmailReminder[];
}

// (A)ccepted or (D)eclined or (u)ndecided
export type GuestStatus = "a" | "d" | "u";

export const formatGuestStatus = (status: GuestStatus | undefined) => {
  switch (status) {
    case "u":
      return "Undecided";
    case "a":
      return "Accepted";
    case "d":
      return "Declined";
  }
  return "Undecided";
};

export interface Guest {
  id: string;
  ventId: string;
  primaryGuestId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  messages?: string;
  updateTime?: string;
  status: GuestStatus;
  userId?: string;
  isUnsubscribed: boolean;
  blockedGuestMessaging: boolean; // true if guest has blocked messaging from other guests
  // Key is custom field ID
  customFieldValues: Record<string, string[]>;
  hostNote: string;
  familyGreeting: string;
  guid: string; // the GUID of the guest, used for unsubscribe email links and personal event links
}

export interface Assignment {
  quantity: number;
  comment: string;
}

// assignment = Assignments[guestId][activityId]
export type Assignments = { [key: string]: { [key: string]: Assignment } };
export const GetAssignmentsForGuest = (
  assignments: Assignments | undefined,
  guestId: string
): { [key: string]: Assignment } => {
  if (assignments == null) return {};
  const v = assignments[guestId];
  if (v == null) return {};
  return v;
};

interface GuestAssignment extends Assignment {
  guestId: string;
}
export const GetAssignmentsList = (
  assignments: Assignments | undefined
): GuestAssignment[] => {
  if (assignments == null) return [];
  let list: GuestAssignment[] = [];
  const guestIds = Object.keys(assignments);
  for (let guestId of guestIds) {
    const activityAssignments = assignments[guestId];
    if (activityAssignments == null) continue;
    const activityIds = Object.keys(activityAssignments);
    for (let activityId of activityIds) {
      list.push({ guestId, ...activityAssignments[activityId] });
    }
  }
  return list;
};

export const GetAssignment = (
  assignments: Assignments | undefined,
  guestId: string,
  activityId: string
): Assignment => {
  const guestAssignments = GetAssignmentsForGuest(assignments, guestId);
  const v = guestAssignments[activityId];
  if (v == null) return { quantity: 0, comment: "" };
  return v;
};

export const GetTotalForActivity = (
  assignments: Assignments | undefined,
  activityId: string
): number => {
  if (assignments == null) return 0;
  let total = 0;
  const guestIds = Object.keys(assignments);
  for (let guestId of guestIds) {
    const activityAssignments = assignments[guestId];
    if (activityAssignments == null) continue;
    const assignment = activityAssignments[activityId];
    if (assignment == null) continue;
    total += assignment.quantity;
  }
  return total;
};
